export class PushCartRecommendations extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.init()
  }

  setupListeners () {
    super.setupListeners()

    this.onCartChanged = this.handleCartChange.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this.onCartChanged,
    )
  }

  async init () {
    this.initializeState()

    await this.fetchInitialCartState()

    this.scheduleRender()
  }

  initializeState () {
    this.isRendering = false
    this._expanded = false
    this.renderTimeoutId = null
    this.cartState = null
    this.lastRecommendationProductId = null
  }

  resetState () {
    this.expanded = false
    this.isRendering = false
    this.cartState = null
    this.lastRecommendationProductId = null
    if (this.renderTimeoutId) {
      window.clearTimeout(this.renderTimeoutId)
      this.renderTimeoutId = null
    }
  }

  scheduleRender () {
    // Cancel any existing timeout to prevent multiple renders
    if (this.renderTimeoutId) {
      window.clearTimeout(this.renderTimeoutId)
      this.renderTimeoutId = null
    }

    if (!this.isRendering) {
      this.renderTimeoutId = window.setTimeout(() => {
        this.renderTimeoutId = null
        this.render()
      }, 250)
    }
  }

  // Get the last added item from cart state
  getLastAddedItem (cartState) {
    if (!cartState?.items || cartState.items.length === 0) {
      return null
    }

    // Sort by key (higher key = more recent) and return the first item
    const sortedItems = [...cartState.items].sort((a, b) => b.key - a.key)
    return sortedItems[0]
  }

  // Build recommendation URL for a specific product
  buildRecommendationUrl (productId, intent) {
    const baseUrl = this.dataset.url
    if (!baseUrl || !productId) return null

    // Replace the product_id parameter or add it if missing
    // eslint-disable-next-line compat/compat
    const url = new URL(baseUrl, window.location.origin)
    url.searchParams.set('product_id', productId)

    if (intent) {
      url.searchParams.set('intent', intent)
    }

    return url.toString()
  }

  // Check if we need to fetch new recommendations
  shouldUpdateRecommendations (cartState) {
    const lastAddedItem = this.getLastAddedItem(cartState)
    const currentProductId = lastAddedItem?.product_id

    // Update if the last added product has changed
    const shouldUpdate = currentProductId && currentProductId !== this.lastRecommendationProductId

    if (shouldUpdate) {
      this.lastRecommendationProductId = currentProductId
    }

    return shouldUpdate
  }

  // Pure functions for recommendation logic
  async fetchRecommendationHtml (url) {
    const response = await fetch(url)

    return response.text()
  }

  parseRecommendationHtml (htmlString) {
    const html = document.createElement('div')
    html.innerHTML = htmlString

    return html
  }

  hasRecommendationItems (htmlElement) {
    return htmlElement.querySelectorAll('[data-rec-item]').length > 0
  }

  updateRecommendationContainers (content) {
    document
      .querySelectorAll('push-cart-recommendations')
      .forEach((container) => {
        container.innerHTML = content
      })
  }

  updateExpandedState (aside, recs, expanded) {
    aside?.setAttribute('aria-expanded', expanded)

    recs?.forEach((rec) => {
      rec.setAttribute('loaded', expanded)
    })
  }

  extractRecommendationContent (htmlElement) {
    const recommendationElement = htmlElement.querySelector('push-cart-recommendations')

    return recommendationElement ? recommendationElement.innerHTML : ''
  }

  async fetchRecommendationsWithFallback (productId) {
    try {
      // Complementary is default intent
      const complementaryUrl = this.buildRecommendationUrl(productId, 'complementary')
      if (!complementaryUrl) return ''

      const complementaryHtml = await this.fetchRecommendationHtml(complementaryUrl)
      const complementaryElement = this.parseRecommendationHtml(complementaryHtml)

      if (this.hasRecommendationItems(complementaryElement)) {
        return this.extractRecommendationContent(complementaryElement)
      }

      // Fallback to related
      const relatedUrl = this.buildRecommendationUrl(productId, 'related')
      if (!relatedUrl) return ''

      const relatedHtml = await this.fetchRecommendationHtml(relatedUrl)
      const relatedElement = this.parseRecommendationHtml(relatedHtml)

      if (this.hasRecommendationItems(relatedElement)) {
        return this.extractRecommendationContent(relatedElement)
      }

      return ''
    } catch (error) {
      console.error('Recommendation fetch error:', error)
      return ''
    }
  }

  hasValidCartData () {
    return this.cartState && this.cartState.items && this.cartState.items.length > 0
  }

  async render () {
    if (this.isRendering) return
    this.isRendering = true

    try {
      if (!this.hasValidCartData()) {
        this.expanded = false

        return
      }

      const lastAddedItem = this.getLastAddedItem(this.cartState)
      if (!lastAddedItem?.product_id) {
        this.expanded = false

        return
      }

      const content = await this.fetchRecommendationsWithFallback(lastAddedItem.product_id)

      if (content) {
        this.updateRecommendationContainers(content)
        this.expanded = true
      } else {
        this.expanded = false
      }
    } catch (error) {
      console.error('Render error:', error)
      this.expanded = false
    } finally {
      this.isRendering = false
    }
  }

  handleCartChange (event) {
    const { state } = event.detail
    if (!state) return

    // Store the new cart state
    this.cartState = state

    // Only render if recommendations should update
    if (this.shouldUpdateRecommendations(state)) {
      this.scheduleRender()
    }
  }

  disconnectedCallback () {
    super.disconnectedCallback()
    this.resetState()
  }

  get expanded () {
    return this._expanded
  }

  set expanded (expanded) {
    this._expanded = expanded

    const aside = document.querySelector('dialog[key="cart"] .drw-Drawer_Aside')
    const recs = document.querySelectorAll('push-cart-recommendations')

    this.updateExpandedState(aside, recs, expanded)
  }

  // Fetch initial cart state for when component loads with existing cart items
  async fetchInitialCartState () {
    try {
      const response = await fetch('/cart.js')
      const cartState = await response.json()
      this.cartState = cartState
      this.lastRecommendationProductId = null // Reset so first render will work
    } catch (error) {
      console.error('Failed to fetch initial cart state:', error)
    }
  }
}

if (!customElements.get('push-cart-recommendations')) {
  customElements.define('push-cart-recommendations', PushCartRecommendations)
}

const { setInitialised } = window.BAO.Carousel.plugins

class PushCartRecommendationsCarousel extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    window.setTimeout(() => this.render(), 500)
  }

  render () {
    if (!this.els?.slide?.exists) return

    if (window.BAO.MediaBreakpoints.isMd) return

    this.carousel = this.setupCarousel()
  }

  setupCarousel () {
    return new window.KeenSlider(
      this.els.slides.element,
      {
        selector: this.els.slide.selector,
        loop: false,
        mode: 'snap',
        slides: {
          perView: 1.1,
          spacing: 15,
        },
      },
      [setInitialised()],
    )
  }
}

if (!customElements.get('push-cart-recommendations-carousel')) {
  customElements.define(
    'push-cart-recommendations-carousel',
    PushCartRecommendationsCarousel,
  )
}
